import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PaymentForm from '../components/PaymentForm';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Dashboard.css';

function UserDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newDomainUrl, setNewDomainUrl] = useState('');
  const [pendingDomain, setPendingDomain] = useState(null);
  const [selectedDomainForRenew, setSelectedDomainForRenew] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // ✅ Modal içindeki hatalar için ayrı state
  const [modalError, setModalError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [domainsRes, statsRes] = await Promise.all([
        axios.get('/user/domains'),
        axios.get('/user/dashboard-stats')
      ]);
      setDomains(domainsRes.data.domains);
      setStats(statsRes.data);
    } catch (error) {
      const errorCode = error.response?.data?.error_code;
      if (errorCode) {
        setError(t(`error.${errorCode}`));
      } else {
        setError(t('error.loadingData'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setModalError(''); // ✅ Modal hatayı temizle
    setError('');
    setSuccess('');
    
    if (!newDomainUrl.trim()) {
      setModalError(t('error.enterDomain')); // ✅ Modal içinde göster
      return;
    }
    
    try {
      setLoading(true);
      
      // ✅ Domain ekle (status: 'pending_verification' olarak oluşturulacak)
      const response = await axios.post('/user/domains', { domainUrl: newDomainUrl });
      setPendingDomain(response.data.domain);
      setShowAddModal(false);
      setShowPaymentModal(true); // PaymentForm içinde verification gösterilecek
      setNewDomainUrl('');
      setModalError('');
      
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      if (errorCode) {
        setModalError(t(`error.${errorCode}`));
      } else {
        setModalError(t('error.addDomain'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (subscription) => {
    setShowPaymentModal(false);
    setSelectedDomainForRenew(null);
    setPendingDomain(null);
    setSuccess(t('success.paymentSuccess'));
    fetchDashboardData();
  };

  const handlePaymentCancel = async () => {
    setShowPaymentModal(false);
    setSelectedDomainForRenew(null);
    
    // Eğer pending domain varsa (yeni eklenen), hemen sil ve listeyi güncelle
    if (pendingDomain) {
      try {
        await axios.delete(`/user/domains/${pendingDomain.id}`);
        setPendingDomain(null);
        // Silindikten hemen sonra listeyi güncelle
        await fetchDashboardData();
      } catch (err) {
        console.error('Failed to delete pending domain:', err);
        // Hata olsa bile listeyi güncelle
        fetchDashboardData();
      }
    }
  };

  const handleRenewSubscription = (domain) => {
    setSelectedDomainForRenew(domain);
    setShowPaymentModal(true);
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm(t('confirm.deleteDomain'))) {
      return;
    }
    try {
      await axios.delete(`/user/domains/${domainId}`);
      setSuccess(t('success.domainDeleted'));
      fetchDashboardData();
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      if (errorCode) {
        setError(t(`error.${errorCode}`));
      } else {
        setError(t('error.deleteDomain'));
      }
    }
  };

  const getSubscriptionBadge = (domain) => {
    if (!domain.subscription_id) {
      return <span className="badge danger">{t('subscription.pending')}</span>;
    }
    const daysRemaining = parseInt(domain.days_remaining);
    if (domain.subscription_status === 'expired' || daysRemaining < 0) {
      return <span className="badge danger">{t('subscription.expired')}</span>;
    }
    if (daysRemaining <= 7) {
      return <span className="badge warning">{t('subscription.daysRemaining', { count: daysRemaining })}</span>;
    }
    return <span className="badge success">{t('subscription.daysRemaining', { count: daysRemaining })}</span>;
  };

  if (loading && domains.length === 0) {
    return (
      <div className="dashboard-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="dashboard-main">
          <div className="loading">{t('loading.text')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div>
            <h1>{t('dashboard.title')}</h1>
            <p>{t('dashboard.welcome')}, {user.full_name || user.email}</p>
          </div>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            {t('domain.addNew')}
          </button>
        </header>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card"><h3>{t('stats.totalDomains')}</h3><p className="stat-number">{stats.total_domains || 0}</p></div>
            <div className="stat-card success"><h3>{t('stats.activeMonitoring')}</h3><p className="stat-number">{stats.active_monitoring || 0}</p></div>
            <div className="stat-card warning"><h3>{t('stats.expiredDomains')}</h3><p className="stat-number">{stats.expired_domains || 0}</p></div>
            <div className="stat-card info"><h3>{t('stats.checks24h')}</h3><p className="stat-number">{stats.checks_24h || 0}</p></div>
            <div className="stat-card danger"><h3>{t('stats.errors24h')}</h3><p className="stat-number">{stats.errors_24h || 0}</p></div>
          </div>
        )}
        <section className="section domains">
          <h2>{t('domains.title')} ({domains.length})</h2>
          {domains.length === 0 ? (
            <div className="empty-state">
              <p>{t('domains.noneAdded')}</p>
              <button className="btn-primary" onClick={() => setShowAddModal(true)}>
                {t('domains.addFirst')}
              </button>
            </div>
          ) : (
            <table className="data-table dashboard_domains">
              <thead>
                <tr>
                  <th scope="col">{t('domain.url')}</th>
                  <th scope="col">{t('subscription.pending')}</th>
                  <th scope="col">{t('domain.monitoring')}</th>
                  <th scope="col">{t('domain.checks24h')}</th>
                  <th scope="col">{t('user.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {domains.map(domain => (
                  <tr key={domain.id}>
                    <td scope="row" data-label={t('domain.url')}>
                      <a href={`/domains/${domain.id}`} onClick={(e) => { e.preventDefault(); navigate(`/domains/${domain.id}`); }}>
                        {domain.domain_url}
                      </a>
                    </td>
                    <td data-label={t('subscription.pending')}>{getSubscriptionBadge(domain)}</td>
                    <td data-label={t('domain.monitoring')}>
                      <span className={`badge ${domain.is_monitoring ? 'success' : 'secondary'}`}>
                        {domain.is_monitoring ? t('filter.active') : t('filter.passive')}
                      </span>
                    </td>
                    <td data-label={t('domain.checks24h')}>
                      <div className="stat-inline">
                        <span className="success-text">{domain.checks_24h || 0} {t('checks.history')}</span>
                        {domain.errors_24h > 0 && <span className="error-text">{domain.errors_24h} {t('errors.recent')}</span>}
                      </div>
                    </td>
                    <td data-label={t('user.actions')} className="table-last-td">
                      <div className="action-buttons">
                        {(!domain.subscription_id || domain.subscription_status === 'expired') ? (
                          <button className="btn-small btn-success" onClick={() => handleRenewSubscription(domain)}>
                            {t('action.payNow')}
                          </button>
                        ) : (
                          <button className="btn-small btn-primary" onClick={() => handleRenewSubscription(domain)}>
                            {t('action.renew')}
                          </button>
                        )}
                        <button className="btn-small btn-secondary" onClick={() => navigate(`/domains/${domain.id}`)}>
                          {t('action.details')}
                        </button>
                        <button className="btn-small btn-danger" onClick={() => handleDeleteDomain(domain.id)}>
                          {t('action.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Domain Ekleme Modalı */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => { setShowAddModal(false); setModalError(''); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('domain.addNew')}</h2>
                <button className="modal-close" onClick={() => { setShowAddModal(false); setModalError(''); }}>×</button>
              </div>

              {/* ✅ Modal içinde hata mesajı gösterimi */}
              {modalError && (
                <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                  {modalError}
                </div>
              )}

              <form onSubmit={handleAddDomain}>
                <div className="form-group">
                  <label htmlFor="domainUrl">{t('domain.url')}</label>
                  <input
                    type="text"
                    id="domainUrl"
                    value={newDomainUrl}
                    onChange={(e) => setNewDomainUrl(e.target.value)}
                    placeholder="örnek: example.com"
                    required
                    autoFocus
                  />
                  <p className="form-help">{t('domain.addHelp')}</p>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => { setShowAddModal(false); setModalError(''); }}>
                    {t('action.cancel')}
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? t('loading.text') : t('action.add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Ödeme Modalı */}
        {showPaymentModal && (pendingDomain || selectedDomainForRenew) && (
          <div className="modal-overlay" style={{padding: '0px'}}>
            <div className="modal-content modal-largex" onClick={(e) => e.stopPropagation()}>
              <PaymentForm
                domain={pendingDomain || selectedDomainForRenew}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default UserDashboard;
